import { PATTERN_MAX_INPUT, inCharSet, parsePattern, type CharSet, type PatternExamples, type PatternNode } from '@eformulieren/model'

/**
 * Een patroon uitvoeren: de regex-escape bij een controle en bij een voorwaarde voor zichtbaarheid
 * (besluit van 1 november 2026, eisenboek 12.43, D-17, D-19).
 *
 * **Geen `RegExp`, maar een automaat.** Een patroon komt van de inrichter en draait in de pod en in de
 * browser van de invuller. `RegExp` zoekt bij sommige patronen exponentieel lang terug, en een lopende
 * `RegExp` is niet te onderbreken: niet in een pure functie, en niet in de browser. Ontwerp 5.3 vraagt een
 * begrensde uitvoeringstijd. Die geeft deze matcher: hij bouwt uit de boom van `parsePattern` een
 * Thompson-automaat en loopt de invoer één keer door, met per teken de verzameling toestanden waarin de
 * automaat kan zijn. Een toestand komt per teken hooguit één keer in die verzameling, dus de rekentijd is
 * ten hoogste het aantal tekens maal het aantal toestanden, en dat laatste begrenst het schema (2000).
 *
 * **Deterministisch in Node en in de browser** (R-01e, ontwerp 5.9). Er hangt niets af van vlaggen, van de
 * taal van de machine of van de versie van de JavaScript-engine; de suite draait twee keer en vergelijkt
 * de uitkomst met `RegExp` met de vlag `u` op willekeurige patronen.
 *
 * Gebruikt door de controle (`check.ts`), de voorwaarde (`conditions.ts`), de poort en het proberen in de
 * configurator, zodat wat de inrichter ziet precies is wat de pod rekent (D-19).
 */

type State =
  | { readonly kind: 'char'; readonly set: CharSet; readonly next: number }
  | { kind: 'split'; first: number; readonly second: number }
  | { readonly kind: 'match' }

interface Program {
  readonly states: readonly State[]
  readonly start: number
}

/** Bouwt de automaat van achter naar voor: elk stuk krijgt de toestand mee waar het daarna heen gaat. */
function compile(node: PatternNode): Program {
  const states: State[] = [{ kind: 'match' }]
  const push = (state: State): number => states.push(state) - 1

  const build = (part: PatternNode, next: number): number => {
    switch (part.kind) {
      case 'char':
        return push({ kind: 'char', set: part.set, next })
      case 'sequence': {
        let at = next
        for (let i = part.parts.length - 1; i >= 0; i -= 1) at = build(part.parts[i] as PatternNode, at)
        return at
      }
      case 'choice': {
        const starts = part.options.map((option) => build(option, next))
        let at = starts[starts.length - 1] as number
        for (let i = starts.length - 2; i >= 0; i -= 1) at = push({ kind: 'split', first: starts[i] as number, second: at })
        return at
      }
      case 'repeat': {
        let at = next
        if (part.max === undefined) {
          // Een lus: de splitsing gaat het stuk in of verder, en het stuk komt terug bij de splitsing.
          const loop = push({ kind: 'split', first: -1, second: next })
          const body = build(part.part, loop)
          const state = states[loop] as { kind: 'split'; first: number }
          state.first = body
          at = loop
        } else {
          // Elk optioneel exemplaar kan worden overgeslagen, en dan ook alle volgende.
          for (let i = 0; i < part.max - part.min; i += 1) at = push({ kind: 'split', first: build(part.part, at), second: next })
        }
        for (let i = 0; i < part.min; i += 1) at = build(part.part, at)
        return at
      }
    }
  }

  const start = build(node, 0)
  return { states, start }
}

/**
 * De gecompileerde patronen, per expressie. Begrensd, zodat een lange sessie in de configurator geen
 * geheugen opstapelt; een patroon dat niet leest staat er als `null`.
 */
const cache = new Map<string, Program | null>()
const CACHE_SIZE = 256

function programOf(expression: string): Program | undefined {
  const known = cache.get(expression)
  if (known !== undefined) return known ?? undefined
  const parsed = parsePattern(expression)
  const program = parsed.ok ? compile(parsed.node) : null
  if (cache.size >= CACHE_SIZE) cache.clear()
  cache.set(expression, program)
  return program ?? undefined
}

function run(program: Program, text: string): boolean {
  const { states } = program
  const seen = new Int32Array(states.length).fill(-1)
  const stack: number[] = []
  let step = 0

  // Alle toestanden die zonder een teken te lezen bereikbaar zijn; de splitsingen zelf tellen niet mee.
  const add = (list: number[], from: number): void => {
    stack.push(from)
    while (stack.length > 0) {
      const at = stack.pop() as number
      if (seen[at] === step) continue
      seen[at] = step
      const state = states[at] as State
      if (state.kind === 'split') {
        stack.push(state.second, state.first)
      } else {
        list.push(at)
      }
    }
  }

  let current: number[] = []
  add(current, program.start)
  for (const char of text) {
    step += 1
    const codePoint = char.codePointAt(0) as number
    const next: number[] = []
    for (const at of current) {
      const state = states[at] as State
      if (state.kind === 'char' && inCharSet(state.set, codePoint)) add(next, state.next)
    }
    current = next
    if (current.length === 0) return false
  }
  return current.some((at) => states[at]?.kind === 'match')
}

/** Of deze tekst meer dan zoveel tekens (code points) heeft, zonder hem helemaal te tellen als het niet hoeft. */
function longerThan(text: string, limit: number): boolean {
  if (text.length <= limit) return false
  let count = 0
  for (const _char of text) {
    count += 1
    if (count > limit) return true
  }
  return false
}

/**
 * Of deze tekst als geheel aan het patroon voldoet: `true` of `false`, of `undefined` als er geen uitkomst
 * is, omdat het patroon niet in de taal valt of de tekst langer is dan 10.000 tekens. Een voorwaarde die
 * op een patroon rekent maakt van geen uitkomst "klopt niet", aan beide kanten (eisenboek 12.43).
 */
export function patternMatch(expression: string, text: string): boolean | undefined {
  const program = programOf(expression)
  if (program === undefined || longerThan(text, PATTERN_MAX_INPUT)) return undefined
  return run(program, text)
}

/** Of deze tekst als geheel aan het patroon voldoet; geen uitkomst is nee. */
export function patternMatches(expression: string, text: string): boolean {
  return patternMatch(expression, text) === true
}

/**
 * Wat er mankeert aan de voorbeelden bij een patroon (D-19, eisenboek 12.43), als bijzinnen: kleine letter,
 * geen punt. Leeg is: geprobeerd en in orde.
 *
 * Proberen is de enige controle op een patroon (D-18, D-19), en daarom een poort en geen advies: minstens
 * één voorbeeld dat past, en minstens één dat niet past, want alleen dat laatste betrapt `.*` of een te
 * ruim patroon. De configurator toont dezelfde zinnen in de dialoog, en de poort weigert erop.
 */
export function patternExampleProblems(expression: string, examples: PatternExamples | undefined): string[] {
  const parsed = parsePattern(expression)
  if (!parsed.ok) return [parsed.reason]
  const problems: string[] = []
  const fitting = examples?.passend ?? []
  const notFitting = examples?.nietPassend ?? []
  if (fitting.length === 0) problems.push('er staat geen voorbeeld dat past; geef er minstens een')
  if (notFitting.length === 0) problems.push('er staat geen voorbeeld dat niet past; geef er minstens een, zodat een te ruim patroon opvalt')
  for (const example of fitting) {
    if (patternMatch(expression, example) !== true) problems.push(`het voorbeeld "${example}" past niet, maar staat bij wat moet passen`)
  }
  for (const example of notFitting) {
    if (patternMatch(expression, example) !== false) problems.push(`het voorbeeld "${example}" past wel, maar staat bij wat niet mag passen`)
  }
  return problems
}
